import {
    Budget,
    BudgetFeeLine,
    BudgetPurchaseLine,
    RelationsKind,
    useApp,
} from "~/core";

type Api = ReturnType<typeof useApp>["api"];

export const getFeeLines = (budget: Budget) =>
    budget.phases.value.flatMap((phase) =>
        phase.lines.value.filter(
            (line): line is BudgetFeeLine => line.t === "A",
        ),
    );

export const getPurchaseLines = (budget: Budget) =>
    budget.phases.value.flatMap((phase) =>
        phase.lines.value.filter(
            (line): line is BudgetPurchaseLine => line.t === "I",
        ),
    );

export const hasPurchaseLines = (budget: Budget) =>
    getPurchaseLines(budget).length > 0;

// a line is used when something was created from it and still exists in the
// project, so deleting a created task/order makes its line available again
export const isFeeLineUsed = (
    api: Api,
    budget: Budget,
    line: BudgetFeeLine,
) => {
    const tasks = api.task.tasks(budget.project.toString()).value;

    return api.relation
        .bySource(RelationsKind.RelationsKindBudgetFee, line._id)
        .value.some((relation) =>
            tasks.some((task) => task._id.equals(relation.target_id)),
        );
};

export const isPurchaseLineUsed = (
    api: Api,
    budget: Budget,
    line: BudgetPurchaseLine,
) => {
    const orders = api.purchase.projectOrders(budget.project.toString()).value;

    return api.relation
        .bySource(RelationsKind.RelationsKindBudgetPurchase, line._id)
        .value.some((relation) =>
            orders.some((order) => order._id.equals(relation.target_id)),
        );
};

export const tasksConversionDone = (api: Api, budget: Budget) =>
    getFeeLines(budget).every((line) => isFeeLineUsed(api, budget, line));

export const purchasesConversionDone = (api: Api, budget: Budget) =>
    getPurchaseLines(budget).every((line) =>
        isPurchaseLineUsed(api, budget, line),
    );

export const isConversionDone = (api: Api, budget: Budget) =>
    tasksConversionDone(api, budget) && purchasesConversionDone(api, budget);
